/**
 * LocationPicker - full-screen map modal for location confirmation before sharing.
 *
 * UX flow:
 *   1. Opens centered on user's GPS coords
 *   2. User drags map to adjust - pin stays fixed in center
 *   3. Reverse geocodes the map center (debounced, 500ms)
 *   4. User taps "Share this spot" → onConfirm({ place, address })
 *
 * Uses Leaflet + OpenStreetMap tiles (no API key needed).
 * Nominatim for reverse geocoding (same as existing spot logic).
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { MapContainer, TileLayer, useMapEvents } from 'react-leaflet'
import '../lib/leafletGuard'
import { useTranslation } from 'react-i18next'
import Icon from './Icon'
import 'leaflet/dist/leaflet.css'

// ── Internal: captures map moveend and forwards center coords ─────────────────

function MapCenterTracker({ onMoveEnd }) {
  const map = useMapEvents({
    moveend: () => {
      const c = map.getCenter()
      onMoveEnd(c.lat, c.lng)
    },
  })
  return null
}

// ── Reverse geocode via Nominatim ─────────────────────────────────────────────

async function reverseGeocode(lat, lng) {
  const resp = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
    { headers: { 'User-Agent': 'Hilads/1.0' } },
  )
  if (!resp.ok) throw new Error('Nominatim failed')
  const data = await resp.json()
  const addr = data.address ?? {}

  // Place name: prefer POI name > district > neighbourhood > city
  const place = addr.amenity
    ?? addr.shop
    ?? addr.tourism
    ?? addr.building
    ?? addr.quarter
    ?? addr.neighbourhood
    ?? addr.suburb
    ?? addr.city_district
    ?? addr.district
    ?? addr.town
    ?? addr.city
    ?? data.display_name?.split(',')[0]
    ?? ''

  // Human address: road + neighbourhood/quarter + city
  const addressParts = [
    addr.road,
    addr.house_number ? undefined : undefined, // street number is already in road
    addr.quarter ?? addr.neighbourhood ?? addr.suburb,
    addr.city ?? addr.town,
  ].filter(Boolean)
  const address = addressParts.join(', ')

  return { place, address }
}

// ── Forward geocode (address search) via Nominatim - same provider ────────────

async function searchPlaces(query) {
  const resp = await fetch(
    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&accept-language=en`,
    { headers: { 'User-Agent': 'Hilads/1.0' } },
  )
  if (!resp.ok) throw new Error('Nominatim search failed')
  const data = await resp.json()
  return (Array.isArray(data) ? data : [])
    .map(d => ({ label: String(d.display_name ?? ''), lat: parseFloat(d.lat), lng: parseFloat(d.lon) }))
    .filter(h => h.label && Number.isFinite(h.lat) && Number.isFinite(h.lng))
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function LocationPicker({ initialLat, initialLng, nickname, onConfirm, onClose }) {
  const { t } = useTranslation('common')
  const [place,   setPlace]   = useState('')
  const [address, setAddress] = useState('')
  const [geocoding, setGeocoding] = useState(true)
  const debounceRef = useRef(null)
  // Track current center for the confirm callback
  const centerRef = useRef({ lat: initialLat, lng: initialLng })
  const mapRef    = useRef(null)   // Leaflet map instance (react-leaflet v4 ref)

  // ── Address search (forward geocode) ──
  const [query,     setQuery]     = useState('')
  const [hits,      setHits]      = useState([])
  const [searching, setSearching] = useState(false)
  const [searched,  setSearched]  = useState(false)
  const searchTimer = useRef(null)
  const searchSeq   = useRef(0)

  const runSearch = useCallback(async (q) => {
    const seq = ++searchSeq.current
    setSearching(true)
    try {
      const results = await searchPlaces(q)
      if (seq !== searchSeq.current) return
      setHits(results)
    } catch {
      if (seq === searchSeq.current) setHits([])
    } finally {
      if (seq === searchSeq.current) { setSearching(false); setSearched(true) }
    }
  }, [])

  const onSearchChange = useCallback((text) => {
    setQuery(text)
    clearTimeout(searchTimer.current)
    const q = text.trim()
    if (q.length < 3) { setHits([]); setSearched(false); setSearching(false); return }
    searchTimer.current = setTimeout(() => runSearch(q), 450)
  }, [runSearch])

  // Pan the map to a searched place; moveend → geocodeCenter updates the label,
  // and the user can still drag afterwards to fine-tune.
  const selectHit = useCallback((hit) => {
    setQuery(hit.label.split(',')[0])
    setHits([])
    setSearched(false)
    centerRef.current = { lat: hit.lat, lng: hit.lng }
    mapRef.current?.setView([hit.lat, hit.lng], 16)
  }, [])

  useEffect(() => () => clearTimeout(searchTimer.current), [])

  const geocodeCenter = useCallback(async (lat, lng) => {
    centerRef.current = { lat, lng }
    setGeocoding(true)
    try {
      const result = await reverseGeocode(lat, lng)
      setPlace(result.place)
      setAddress(result.address)
    } catch (e) {
      console.error('[loc-picker] geocode failed:', e)
    } finally {
      setGeocoding(false)
    }
  }, [])

  // Initial geocode on mount
  useEffect(() => {
    geocodeCenter(initialLat, initialLng)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  function handleMoveEnd(lat, lng) {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => geocodeCenter(lat, lng), 600)
  }

  function handleConfirm() {
    const label = place || address || t('locationPicker.yourLocation')
    onConfirm({ place: label, address, lat: centerRef.current.lat, lng: centerRef.current.lng })
  }

  return (
    <div className="loc-picker-overlay">
      <div className="loc-picker">

        {/* ── Header ── */}
        <div className="loc-picker-header">
          <span className="loc-picker-title">{t('locationPicker.title')}</span>
          <button className="loc-picker-close" onClick={onClose} aria-label={t('locationPicker.close')}><Icon name="close" size={20} /></button>
        </div>

        {/* ── Map ── */}
        <div className="loc-picker-map-wrap">
          <MapContainer
            ref={mapRef}
            center={[initialLat, initialLng]}
            zoom={16}
            style={{ width: '100%', height: '100%' }}
            zoomControl={false}
            attributionControl={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <MapCenterTracker onMoveEnd={handleMoveEnd} />
          </MapContainer>

          {/* ── Address search overlay ── */}
          <div className="loc-picker-search">
            <div className="loc-picker-search-box">
              <span className="loc-picker-search-icon" aria-hidden="true"><Icon name="search-outline" size={16} /></span>
              <input
                className="loc-picker-search-input"
                value={query}
                onChange={e => onSearchChange(e.target.value)}
                placeholder={t('locationPicker.search')}
                autoComplete="off"
                spellCheck="false"
              />
              {query.length > 0 && (
                <button
                  className="loc-picker-search-clear"
                  onClick={() => { setQuery(''); setHits([]); setSearched(false) }}
                  aria-label={t('locationPicker.clearSearch')}
                ><Icon name="close" size={16} /></button>
              )}
            </div>
            {(hits.length > 0 || (searched && !searching)) && (
              <div className="loc-picker-suggestions">
                {hits.length > 0 ? hits.map((hit, i) => (
                  <button
                    key={`${hit.lat},${hit.lng},${i}`}
                    className="loc-picker-suggestion"
                    onClick={() => selectHit(hit)}
                  >
                    <Icon name="location-outline" size={16} /> <span>{hit.label}</span>
                  </button>
                )) : (
                  <div className="loc-picker-no-results">{t('locationPicker.noResults')}</div>
                )}
              </div>
            )}
          </div>

          {/* Fixed pin - stays centered, map moves underneath */}
          <div className="loc-picker-pin" aria-hidden="true" style={{ color: 'var(--primary)' }}><Icon name="location" size={36} style={{ display: 'block' }} /></div>

          {/* Drag hint */}
          <div className="loc-picker-hint">{t('locationPicker.moveHint')}</div>
        </div>

        {/* ── Footer: address + actions ── */}
        <div className="loc-picker-footer">
          <div className="loc-picker-address-card">
            {geocoding ? (
              <span className="loc-picker-geocoding">{t('locationPicker.loading')}</span>
            ) : (
              <>
                <span className="loc-picker-place">{place || t('locationPicker.yourLocation')}</span>
                {address && <span className="loc-picker-addr">{address}</span>}
              </>
            )}
          </div>

          <button
            className="loc-picker-confirm"
            onClick={handleConfirm}
            disabled={geocoding}
          >
            {t('locationPicker.confirm')}
          </button>
          <button className="loc-picker-cancel" onClick={onClose}>
            {t('cancel')}
          </button>
        </div>

      </div>
    </div>
  )
}
